import { Routes } from 'src/utils/enums';

const CLI_LOGIN_REQUEST_KEY = 'cliLoginRequest';
const CLI_LOGIN_PAYLOAD_VERSION = 1;
// Matches the CLI's loopback timeout; an older stash points at a dead listener.
const CLI_LOGIN_STASH_TTL_MS = 5 * 60 * 1000;

export interface CliLoginRequest {
  redirectUri: string;
  state: string;
}

export interface CliLoginPayload {
  api_url: string;
  cognito: {
    client_id: string;
    endpoint_url?: string;
    region: string;
    user_pool_id: string;
  };
  dashboard_url: string;
  email: string;
  id_token: string;
  refresh_token: string;
  state: string;
  v: number;
}

interface CognitoAuthConfig {
  endpoint?: string;
  region?: string;
  userPoolId?: string;
  userPoolWebClientId?: string;
}

export function isValidCliRedirectUri(redirectUri: string): boolean {
  let url: URL;
  try {
    url = new URL(redirectUri);
  } catch {
    return false;
  }
  return (
    url.protocol === 'http:' &&
    (url.hostname === '127.0.0.1' || url.hostname === 'localhost') &&
    url.port !== '' &&
    url.pathname === '/callback' &&
    !url.username &&
    !url.password
  );
}

export function getCliLoginRequest(search: string): CliLoginRequest | null {
  const params = new URLSearchParams(search);
  const redirectUri = params.get('redirect_uri');
  const state = params.get('state');
  if (!redirectUri || !state) {
    return null;
  }
  return { redirectUri, state };
}

export function stashCliLoginRequest() {
  if (!window.location.pathname.endsWith(Routes.CLI_LOGIN)) {
    return;
  }
  const request = getCliLoginRequest(window.location.search);
  if (request) {
    sessionStorage.setItem(
      CLI_LOGIN_REQUEST_KEY,
      JSON.stringify({ ...request, stashedAt: Date.now() }),
    );
  }
}

export function clearStashedCliLoginRequest() {
  sessionStorage.removeItem(CLI_LOGIN_REQUEST_KEY);
}

export function takeStashedCliLoginPath(): string | null {
  const raw = sessionStorage.getItem(CLI_LOGIN_REQUEST_KEY);
  if (!raw) {
    return null;
  }
  clearStashedCliLoginRequest();
  try {
    const { redirectUri, stashedAt, state } = JSON.parse(
      raw,
    ) as CliLoginRequest & {
      stashedAt?: number;
    };
    if (!stashedAt || Date.now() - stashedAt > CLI_LOGIN_STASH_TTL_MS) {
      return null;
    }
    const query = new URLSearchParams({ redirect_uri: redirectUri, state });
    return `${Routes.CLI_LOGIN}?${query.toString()}`;
  } catch {
    return null;
  }
}

// Amplify's region is an availability zone (us-west-2a); AWS pool IDs carry the real region.
function regionForPool(userPoolId: string, region = ''): string {
  const prefix = userPoolId.split('_')[0];
  if (/^[a-z]{2}(-[a-z]+)+-\d+$/.test(prefix)) {
    return prefix;
  }
  return region.replace(/^([a-z]{2}(-[a-z]+)+-\d+)[a-z]$/, '$1');
}

function absoluteUrl(url: string, origin: string): string {
  return new URL(url, origin).toString().replace(/\/$/, '');
}

export function buildCliLoginPayload({
  apiUrl,
  authConfig,
  basePath,
  email,
  idToken,
  origin,
  refreshToken,
  state,
}: {
  apiUrl: string;
  authConfig: CognitoAuthConfig;
  basePath: string;
  email: string;
  idToken: string;
  origin: string;
  refreshToken: string;
  state: string;
}): CliLoginPayload {
  const userPoolId = authConfig.userPoolId ?? '';
  return {
    api_url: absoluteUrl(apiUrl, origin),
    cognito: {
      client_id: authConfig.userPoolWebClientId ?? '',
      region: regionForPool(userPoolId, authConfig.region),
      user_pool_id: userPoolId,
      ...(authConfig.endpoint
        ? { endpoint_url: absoluteUrl(authConfig.endpoint, origin) }
        : {}),
    },
    dashboard_url: `${origin}${basePath}`,
    email,
    id_token: idToken,
    refresh_token: refreshToken,
    state,
    v: CLI_LOGIN_PAYLOAD_VERSION,
  };
}

// A top-level form POST, not fetch: no CORS or local-network preflight, and the tokens stay out of the URL.
export function submitCliLoginForm(
  redirectUri: string,
  state: string,
  payload: CliLoginPayload,
) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = redirectUri;
  const fields = { payload: JSON.stringify(payload), state };
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}
